/**
 * Route safety check result — the last "Check route" run, held so two places can
 * read it: the Route sheet (which shows the plain-English summary) and the map
 * (which highlights the flagged legs on the chart). Keeping it in its own small
 * store means the checker runs once on tap and both surfaces stay in step.
 *
 * The stored value is a single object reference that only changes when a new
 * check runs or we clear it. That matters: the map subscribes with a plain
 * selector, so a stable reference avoids the re-render loop you get when a
 * selector hands back a freshly built object every render.
 */
import { create } from 'zustand';
import type { RouteSafetyResult } from '@/routing';

export interface RouteSafetyCheck {
  /** Which route this result belongs to, so a stale result for another route is ignored. */
  routeId: string;
  result: RouteSafetyResult;
  /** When the check ran, epoch ms, for a "checked just now" note. */
  atMs: number;
}

interface RouteSafetyState {
  check: RouteSafetyCheck | null;
  setCheck: (routeId: string, result: RouteSafetyResult) => void;
  clear: () => void;
}

export const useRouteSafetyStore = create<RouteSafetyState>((set) => ({
  check: null,
  setCheck: (routeId, result) => set({ check: { routeId, result, atMs: Date.now() } }),
  clear: () => set({ check: null }),
}));
